<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';
import { GetAllGroupsService, GetAnimalGroupTypesService } from '@AnimalModule';

const props = defineProps<{
  error?: string;
  field?: FieldSchema;
  placeholder?: string;
  required?: boolean;
}>();

const { t } = useI18n();

const model = defineModel();

const groupSelect = ref<'new' | 'existing'>('new');
const existingGroupModel = ref();
const groupNameModel = ref();
const groupTypeModel = ref();
const groupDescriptionModel = ref();
const groupColorModel = ref();
const optionsGroups = ref();
const optionsTypes = ref();

const isDuplicatedAnimal = computed(() => props.field?.settings?.isDuplicatedAnimal);
const firstAnimalGroupId = computed(() => props.field?.settings?.firstAnimalGroupId);
const isGroupLinkedForm = computed(() => props.field?.settings?.isGroupLinkedForm);

const options = computed(() => [
  {
    value: 'new',
    name: t('animals.animalGroups.fields.label.newGroup'),
  },
  {
    value: 'existing',
    name: t('animals.animalGroups.fields.label.existingGroup'),
  },
]);

onMounted(() => getTypesAndGroups());

watch([groupColorModel, groupNameModel], () => updateGroupModel());
watch([isDuplicatedAnimal, isGroupLinkedForm], () => getTypesAndGroups());
watch(
  () => props.field?.settings,
  () => {
    getTypesAndGroups();
  },
  { deep: true, immediate: true }
);

function resetModels() {
  existingGroupModel.value = null;
  groupNameModel.value = null;
  groupTypeModel.value = null;
  groupDescriptionModel.value = null;
  groupColorModel.value = null;
}

function getTypesAndGroups() {
  resetModels();

  GetAnimalGroupTypesService().then((results) => (optionsTypes.value = results?.data));
  GetAllGroupsService('active')
    .then((results) => {
      optionsGroups.value = results?.data
        ?.map((group: any) => ({
          ...group,
          label: `${group.seq_id} - ${group.label}`,
        }))
        ?.sort((a: any, b: any) => b.seq_id - a.seq_id);
    })
    .finally(() => {
      if (isDuplicatedAnimal.value || isGroupLinkedForm.value) {
        groupSelect.value = 'existing';
        existingGroupModel.value = optionsGroups.value?.find(
          (group: any) => group._id?.$oid === firstAnimalGroupId.value
        );
        updateGroupModel();
      }
    });
}

function updateGroupModel() {
  if (groupSelect.value === 'existing') return (model.value = existingGroupModel.value);
  if (!groupNameModel.value || !groupTypeModel.value) return (model.value = null);

  model.value = {
    name: groupNameModel.value,
    type: groupTypeModel.value,
    description: groupDescriptionModel.value,
    color: groupColorModel.value,
    option: groupSelect.value,
  };
}
</script>

<template>
  <AFieldRadio
    v-if="optionsGroups && optionsGroups.length && !isDuplicatedAnimal && !isGroupLinkedForm"
    v-model="groupSelect"
    :error
    :options
    :placeholder="field?.settings.placeholder || placeholder"
    :required="field?.settings.required || required"
  />

  <div
    v-if="groupSelect === 'existing'"
    :class="['flex w-full flex-col gap-1', isDuplicatedAnimal ? 'mt-0' : 'mt-5']"
  >
    <AFieldLabel
      v-if="!isDuplicatedAnimal && !isGroupLinkedForm"
      class="mr-10 font-semibold"
      :error
      :label="$t('animals.animalGroups.fields.label.list')"
      :required="field?.settings.required || required"
    />

    <AFieldDropdown
      v-model="existingGroupModel"
      hasFilter
      showClear
      optionLabel="label"
      :error
      :disabled="isGroupLinkedForm"
      :options="optionsGroups"
      :placeholder="field?.settings.placeholder || placeholder"
      :required="field?.settings.required || required"
      @change="updateGroupModel"
    />
  </div>

  <div v-else-if="groupSelect === 'new' && !isDuplicatedAnimal && !isGroupLinkedForm">
    <div
      :class="[
        'flex w-full flex-col gap-1',
        !optionsGroups || !optionsGroups.length ? 'mt-0' : 'mt-5',
      ]"
    >
      <AFieldLabel
        v-if="optionsGroups && optionsGroups.length"
        class="mr-10 font-semibold"
        :error
        :label="$t('animals.animalGroups.fields.label.name')"
        :required="field?.settings.required || required"
      />

      <AFieldInputText
        v-model="groupNameModel"
        :error
        :placeholder="$t('animals.animalGroups.fields.placeholder.name')"
        :required="field?.settings.required || required"
        @change="updateGroupModel"
      />
    </div>

    <div class="mt-5 flex w-full flex-col gap-1">
      <AFieldLabel
        class="mr-10 font-semibold"
        :error
        :label="$t('animals.animalGroups.fields.label.type')"
        :required="field?.settings.required || required"
      />

      <AFieldDropdown
        v-model="groupTypeModel"
        optionLabel="label"
        :error
        :options="optionsTypes"
        :placeholder="$t('animals.animalGroups.fields.placeholder.type')"
        :required="field?.settings.required || required"
        @change="updateGroupModel"
      />
    </div>

    <div class="mt-5 flex w-full flex-col gap-1">
      <AFieldLabel
        class="mr-10 font-semibold"
        :label="$t('animals.animalGroups.fields.label.description')"
      />

      <AFieldTextArea
        v-model="groupDescriptionModel"
        :placeholder="$t('animals.animalGroups.fields.placeholder.description')"
        @change="updateGroupModel"
      />
    </div>

    <div class="mt-5 flex w-full flex-col gap-1">
      <AFieldLabel
        class="mr-10 font-semibold"
        :label="$t('animals.animalGroups.fields.label.color')"
      />

      <AFieldColorSelector
        v-model="groupColorModel"
        preDefinedColors
      />
    </div>
  </div>
</template>
